import { datadogLogs } from '@datadog/browser-logs';

declare global {
  interface Window {
    FULLSTORY_ORG_ID: string;
    ENV?: 'dev' | 'staging' | 'production';
    APP_VERSION?: string;
  }
}

export interface FullstoryConfig {
  orgId: string;
  enabled: boolean;
  environment?: string;
  version?: string;
  debug?: boolean;
  recordCrossDomainIFrames?: boolean;
  recordOnlyThisIFrame?: boolean;
  devMode?: boolean;
}

/**
 * List of domains that are disabled for Fullstory initialization
 */

export const FULLSTORY_DISABLED_DOMAINS = ['disney.checkbox.ai'] as const;

/**
 * Checks if the current domain is disabled for Fullstory initialization
 * @returns true if the domain is disabled, false otherwise
 */
export const isDomainDisabled = (): boolean => {
  const hostname = window.location.hostname;

  if (!hostname || typeof hostname !== 'string') {
    // do not initialize fullstory for invalid host
    return true;
  }

  // check for exact match in disabled domains
  return FULLSTORY_DISABLED_DOMAINS.some((domain) => domain === hostname);
};

/**
 * Gets the Fullstory configuration based on environment and window variables
 * @returns Fullstory configuration object
 */
export const getFullstoryConfig = (): FullstoryConfig | null => {
  const orgId = window.FULLSTORY_ORG_ID;
  if (!orgId) {
    // eslint-disable-next-line no-console
    console.warn(
      'Fullstory: No organization ID provided. Fullstory will not be initialized.'
    );
    // Only use datadogLogs if Datadog is enabled
    if (window.DATADOG_RUM_ENABLED) {
      datadogLogs.logger.warn(
        'Fullstory: No organization ID provided. Fullstory will not be initialized.'
      );
    }

    return null;
  }

  return {
    orgId,
    enabled: true,
    environment: window.ENV,
    version: window.APP_VERSION,
    debug: window.ENV === 'dev',
    recordCrossDomainIFrames: false,
    recordOnlyThisIFrame: false,
    devMode: window.ENV === 'dev',
  };
};

/**
 * Validates if Fullstory should be initialized based on presence of organization ID
 * @returns true if Fullstory should be initialized, false otherwise
 */
export const shouldInitializeFullstory = (): boolean => {
  const orgId = window.FULLSTORY_ORG_ID;
  return orgId !== null && orgId !== undefined;
};
